import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { useDarkMode } from '../../hooks/useDarkMode';

const DashboardHeaderLanding = () => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [isDark, setIsDark] = useDarkMode(); 
  
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);

  const languages = [
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'bs', name: 'Bosanski', flag: '🇧🇦' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ];

  // --- LOGIKA ZA AUTOMATSKU DETEKCIJU JEZIKA ---
  useEffect(() => {
    const detectLocation = async () => {
      const hasSetLanguageManual = localStorage.getItem('i18nextLngManual');
      if (hasSetLanguageManual) return;

      try {
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) throw new Error('Mreža nije dostupna');
        
        const data = await response.json();
        if (!data || !data.country_code) return;

        const country = data.country_code;
        let detectedLang = 'en';

        if (country === 'IT') {
          detectedLang = 'it';
        } else if (['BA', 'RS', 'HR', 'ME'].includes(country)) {
          detectedLang = 'bs';
        } else if (['DE', 'AT', 'CH'].includes(country)) {
          detectedLang = 'de';
        } else {
          detectedLang = 'en';
        }

        if (i18n.language !== detectedLang) {
          i18n.changeLanguage(detectedLang);
        }
      } catch (error) {
        console.error("Automatska detekcija lokacije nije uspjela:", error);
      }
    };

    detectLocation();
  }, [i18n]);

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('i18nextLngManual', 'true'); 
    setShowLangMenu(false);
  };

  // --- LOGIKA ZA TEME ---
  const changeTheme = (themeName) => {
    const body = document.body;
    const themes = ['theme-green', 'theme-black', 'theme-purple', 'theme-orange'];
    themes.forEach(theme => body.classList.remove(theme));
    
    if (themeName !== 'default') {
      body.classList.add(`theme-${themeName}`);
      localStorage.setItem('selected-theme', `theme-${themeName}`);
    } else {
      localStorage.setItem('selected-theme', 'default');
    }
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('selected-theme');
    if (savedTheme && savedTheme !== 'default') {
      document.body.classList.add(savedTheme);
    }
  }, []);

  const langMenuRef = useRef(null);
  const settingsMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) setShowLangMenu(false);
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target)) setShowSettingsMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <header className="header" role="banner">
      <div className="header-container relative flex items-center">
        
        {/* LOGO I NASLOV - Skriveni na mobitelu (hidden), vidljivi tek od sm (640px) pa naviše */}
        <div className="header-logo select-none hidden sm:flex">
          <div className="header-logo-icon">
            <div className="relative flex items-center justify-center">
              <Icon name="Shield" size={24} color="var(--color-primary)" strokeWidth={2} />
              <span className="absolute inset-0 flex items-center justify-center font-black text-primary" style={{ fontSize: '11px', transform: 'translateY(-5%)', lineHeight: 1 }}>O</span>
            </div>
          </div>
          <span className="header-logo-text">
            OrizonDesk
          </span>
        </div>

        {/* NAVIGACIJA - Na mobitelu nema margina (w-full), na desktopu ide desno (ml-auto) */}
        <nav className="header-actions flex items-center justify-between sm:justify-end w-full sm:w-auto sm:ml-auto gap-2 md:gap-3">
          
          <button 
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 px-4 md:px-5 py-2.5 bg-primary text-white border-2 border-primary hover:bg-transparent hover:text-primary rounded-xl transition-all duration-300 group shadow-lg shadow-primary/20"
          >
            <Icon name="LogIn" size={18} className="transition-transform group-hover:translate-x-1" />
            <span className="text-[11px] font-black uppercase tracking-widest">
              {t('auth.access_portal')}
            </span>
          </button>

          <div className="flex items-center gap-2 md:gap-3">
            <div className="h-6 w-[1px] bg-border mx-1 opacity-50"></div>

            {/* Settings Menu */}
            <div className="relative" ref={settingsMenuRef}>
              <button onClick={() => setShowSettingsMenu(!showSettingsMenu)} className={`header-action-button ${showSettingsMenu ? 'bg-primary/10 text-primary' : ''}`}>
                <Icon name="Settings" size={20} />
              </button>
              {showSettingsMenu && (
                <div className="absolute top-12 right-0 bg-card rounded-xl shadow-xl border border-border z-[1000] min-w-[220px] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                  <div className="p-4">
                    <p className="text-[10px] font-black uppercase text-muted-foreground mb-3 tracking-widest flex items-center gap-2">
                      <Icon name="Palette" size={12} /> {t('settings.theme_title')}
                    </p>
                    <div className="grid grid-cols-5 gap-2 mb-4">
                      <button onClick={() => changeTheme('default')} className="w-6 h-6 rounded-full bg-[#1E3A5F] border border-border hover:scale-125 transition-transform" />
                      <button onClick={() => changeTheme('green')} className="w-6 h-6 rounded-full bg-[#059669] border border-border hover:scale-125 transition-transform" />
                      <button onClick={() => changeTheme('black')} className="w-6 h-6 rounded-full bg-[#111827] border border-border hover:scale-125 transition-transform" />
                      <button onClick={() => changeTheme('purple')} className="w-6 h-6 rounded-full bg-[#7C3AED] border border-border hover:scale-125 transition-transform" />
                      <button onClick={() => changeTheme('orange')} className="w-6 h-6 rounded-full bg-[#EA580C] border border-border hover:scale-125 transition-transform" />
                    </div>
                    <div className="h-[1px] bg-border w-full my-4" />
                    <button onClick={() => setIsDark(!isDark)} className="w-full flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors border border-border">
                      <div className="flex items-center gap-2">
                        <Icon name={isDark ? "Sun" : "Moon"} size={16} className={isDark ? "text-yellow-500" : "text-primary"} />
                        <span className="text-xs font-bold uppercase tracking-wider">{isDark ? t('settings.light_mode') : t('settings.dark_mode')}</span>
                      </div>
                      <div className={`w-8 h-4 rounded-full relative transition-colors ${isDark ? 'bg-primary' : 'bg-gray-300'}`}>
                        <div className={`absolute top-1 bg-white w-2 h-2 rounded-full transition-transform ${isDark ? 'translate-x-5' : 'translate-x-1'}`} />
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Language Menu */}
            <div className="relative" ref={langMenuRef}>
              <button onClick={() => setShowLangMenu(!showLangMenu)} className="header-action-button flex items-center gap-2 px-2 hover:bg-muted transition-colors rounded-xl">
                <span className="text-xl">{currentLang.flag}</span>
                <Icon name="ChevronDown" size={14} className={showLangMenu ? 'rotate-180' : ''} />
              </button>
              {showLangMenu && (
                <div className="absolute top-12 right-0 bg-card rounded-lg shadow-xl border border-border z-[1000] min-w-[160px] py-2">
                  {languages.map((lang) => (
                    <button key={lang.code} onClick={() => changeLanguage(lang.code)} className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-muted ${i18n.language === lang.code ? 'bg-primary/5 text-primary font-bold' : ''}`}>
                      <span className="text-xl">{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default DashboardHeaderLanding;